import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Card, Button, Spinner, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import {
  getDocumentDetail,
  getFileDownloadUrl
} from 'helpers/documentManagerApi';
import evaluateConditions from 'helpers/evaluateConditions';
import moment from 'moment';

const DocumentDetail = ({ documentId, onBack, onEdit }) => {
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        setLoading(true);
        const res = await getDocumentDetail(documentId);
        if (res && res.status) {
          setDocument(res.data);
        } else {
          toast.error(res?.message || 'Failed to fetch document details');
          onBack();
        }
      } catch (err) {
        console.error(err);
        toast.error('Error fetching document details');
        onBack();
      } finally {
        setLoading(false);
      }
    };

    if (documentId) {
      fetchDetail();
    }
  }, [documentId, onBack]);

  if (loading) {
    return (
      <div className="border-0 text-center py-5">
        <Card.Body>
          <Spinner animation="border" variant="primary" />
          <p className="mt-2 text-600 mb-0">Loading document details...</p>
        </Card.Body>
      </div>
    );
  }

  if (!document) return null;

  const {
    title,
    description,
    type_name,
    created,
    created_by_name,
    fields,
    field_values,
    files
  } = document;

  return (
    <div className="border-0">
      <Card.Header className="bg-light px-0 py-2 mb-3 border-0 d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-2">
          <Button
            variant="link"
            className="p-0 text-secondary"
            onClick={onBack}
            title="Go Back"
          >
            <FontAwesomeIcon icon="arrow-left" className="fs-8" />
          </Button>
          <h5 className="mb-0">Document Details</h5>
        </div>
        <Button variant="primary" size="sm" onClick={onEdit}>
          <FontAwesomeIcon icon="edit" className="me-1" /> Edit
        </Button>
      </Card.Header>
      <Card.Body className="p-0">
        <div className="mb-4">
          <h4 className="text-800 font-sans-serif">{title}</h4>
          <div className="text-500 fs-11 mt-1">
            <span className="me-3">
              <strong>Type:</strong> {type_name}
            </span>
            <span className="me-3">
              <strong>Uploaded By:</strong> {created_by_name}
            </span>
            <span>
              <strong>Date:</strong>{' '}
              {created ? moment(created).format('MM/DD/YYYY hh:mm A') : ''}
            </span>
          </div>
        </div>

        {description && (
          <div className="mb-4 p-3 bg-light rounded border">
            <h6 className="text-700 fs-10 mb-2">Description / Notes</h6>
            <p
              className="mb-0 fs-10 text-800"
              style={{ whiteSpace: 'pre-wrap' }}
            >
              {description}
            </p>
          </div>
        )}

        {fields && fields.length > 0 && (
          <div className="mb-4">
            <h6 className="text-700 fs-10 mb-3">Metadata Parameters</h6>
            <Table
              responsive
              bordered
              hover
              size="sm"
              className="fs-10 align-middle"
            >
              <thead className="bg-200 text-900">
                <tr>
                  <th style={{ width: '40%' }}>Parameter</th>
                  <th style={{ width: '60%' }}>Value</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const visibleFields = fields
                    .sort((a, b) => a.sort_order - b.sort_order)
                    .filter(f =>
                      evaluateConditions(f.conditions, field_values)
                    );

                  return visibleFields.map(field => {
                    const val = field_values[field.field_key];
                    let displayVal = val;

                    if (field.type === 'checkbox') {
                      displayVal = val ? 'Yes' : 'No';
                    } else if (field.type === 'date' && val) {
                      displayVal = moment(val).format('MM/DD/YYYY');
                    } else if (
                      field.type === 'file' ||
                      field.type === 'file_or_text'
                    ) {
                      if (val?.mode === 'text') {
                        displayVal = val.value || (
                          <em className="text-400">Not Provided</em>
                        );
                      } else if (val?.mode === 'file') {
                        const fieldFile =
                          document.field_files?.[field.field_key];
                        if (fieldFile && fieldFile.preview_url) {
                          displayVal = (
                            <a
                              href={fieldFile.preview_url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <FontAwesomeIcon icon="file" className="me-2" />
                              {fieldFile.filename}
                            </a>
                          );
                        } else {
                          displayVal = (
                            <em className="text-400">File attached</em>
                          );
                        }
                      } else {
                        displayVal = <em className="text-400">Not Provided</em>;
                      }
                    } else if (
                      val === undefined ||
                      val === null ||
                      val === ''
                    ) {
                      displayVal = <em className="text-400">Not Provided</em>;
                    }

                    return (
                      <tr key={field.field_key}>
                        <td className="fw-semi-bold text-700">{field.label}</td>
                        <td>{displayVal}</td>
                      </tr>
                    );
                  });
                })()}
              </tbody>
            </Table>
          </div>
        )}

        {files && files.length > 0 ? (
          <div>
            <h6 className="text-700 fs-10 mb-3">Attached Files</h6>
            <div className="d-flex flex-column gap-2">
              {files.map(file => (
                <div
                  key={file.id}
                  className="d-flex align-items-center justify-content-between p-3 border rounded bg-card hover-actions-trigger"
                >
                  <div className="d-flex align-items-center gap-3">
                    <div className="bg-light p-2 rounded border">
                      <FontAwesomeIcon
                        icon="file"
                        className="fs-6 text-primary"
                      />
                    </div>
                    <div>
                      <h6 className="mb-1 text-900 fs-10">{file.filename}</h6>
                      <span className="text-500 fs-11">
                        {(file.file_size / 1024).toFixed(1)} KB |{' '}
                        {file.mime_type}
                      </span>
                    </div>
                  </div>
                  <a
                    href={(file.preview_url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-outline-secondary btn-sm px-3"
                    title="Download File"
                  >
                    <FontAwesomeIcon
                      icon="cloud-download-alt"
                      className="me-2"
                    />
                    Download
                  </a>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-4 border border-dashed rounded bg-light">
            <FontAwesomeIcon icon="file" className="fs-5 text-300 mb-2" />
            <p className="text-500 mb-0 fs-10">
              No attachments found for this document.
            </p>
          </div>
        )}
      </Card.Body>
    </div>
  );
};

DocumentDetail.propTypes = {
  documentId: PropTypes.number.isRequired,
  onBack: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired
};

export default DocumentDetail;
